/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './HODDashboard.css';

// --- SVG Icons ---
const Icons = {
    Print: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Download: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
    Refresh: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>,
    Student: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    Book: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>,
    Check: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
    Alert: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
    Info: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

export default function HODDashboard() {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const authToken = localStorage.getItem('auth_token');
            const schoolId = localStorage.getItem('school_id');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/fetch_hod_analytics.php?school_id=${schoolId}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data.success) {
                setAnalyticsData(response.data);
            } else {
                setError('Failed to load analytics data');
            }
        } catch (err) {
            setError('Failed to fetch analytics.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="hod-dashboard loading-state">
                <div className="spinner"></div>
                <p>Loading Analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="hod-dashboard error-state">
                <p>{error}</p>
                <button onClick={fetchAnalytics} className="btn-retry">Retry</button>
            </div>
        );
    }

    const exportToCSV = () => {
        if (!analyticsData) return;
        let csv = 'HOD Analytics Report\n\nPASS RATES BY SUBJECT\nSubject,Grade,Total,Passed,Failed,Pass %,Avg %\n';

        analyticsData.pass_rates_by_subject.forEach(s => {
            const failed = s.total_students - s.passed_students;
            csv += `${s.subject_name},${s.grade_level},${s.total_students},${s.passed_students},${failed},${s.pass_rate}%,${s.average_percentage}%\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `HOD_Report_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const {
        overall_stats,
        pass_rates_by_subject,
        performance_by_grade,
        top_performers,
        top_performers_by_grade,
        bottom_performers,
        bottom_performers_by_grade,
        students_needing_support_by_subject,
        subject_distribution,
        insights
    } = analyticsData;

    // Group subjects
    const groupedPassRates = Object.values(
        pass_rates_by_subject.reduce((acc, subject) => {
            const key = `${subject.subject_name}_${subject.grade_level}`;
            if (!acc[key]) {
                acc[key] = {
                    ...subject,
                    display_name: `${subject.subject_name} (Gr ${subject.grade_level})`,
                    total_percentage: subject.average_percentage * subject.total_students,
                    class_count: 1
                };
            } else {
                acc[key].total_students += subject.total_students;
                acc[key].passed_students += subject.passed_students;
                acc[key].total_percentage += subject.average_percentage * subject.total_students;
                acc[key].class_count += 1;
            }
            return acc;
        }, {})
    ).map(g => ({
        ...g,
        pass_rate: g.total_students > 0 ? ((g.passed_students / g.total_students) * 100).toFixed(1) : 0,
        average_percentage: g.total_students > 0 ? (g.total_percentage / g.total_students).toFixed(1) : 0
    }));

    // Get personalized greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        const username = localStorage.getItem('username') || 'User';
        let greeting = '';

        if (hour < 12) {
            greeting = 'Good morning';
        } else if (hour < 17) {
            greeting = 'Good afternoon';
        } else {
            greeting = 'Good evening';
        }

        return `${greeting}, ${username}!`;
    };

    return (
        <div className="hod-dashboard">
            <div className="dashboard-header">
                <div>
                    <h1>{getGreeting()}</h1>
                    <p className="subtitle">School Performance Overview</p>
                </div>
                <div className="header-actions no-print">
                    <button onClick={() => window.print()} className="action-btn"><Icons.Print /> Print</button>
                    <button onClick={exportToCSV} className="action-btn"><Icons.Download /> CSV</button>
                    <button onClick={fetchAnalytics} className="action-btn"><Icons.Refresh /> Refresh</button>
                </div>
            </div>

            {/* Insights */}
            {insights && insights.length > 0 && (
                <div className="insights-grid">
                    {insights.map((insight, index) => (
                        <div key={index} className={`insight-card ${insight.type}`}>
                            <div className="insight-icon">
                                {insight.type === 'warning' ? <Icons.Alert /> : <Icons.Info />}
                            </div>
                            <div>
                                <h3>{insight.title}</h3>
                                <p>{insight.message}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Key Metrics */}
            <div className="stats-cards">
                <div className="stat-card">
                    <div className="stat-icon-wrapper blue"><Icons.Student /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_students}</h3>
                        <p>Total Students</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper green"><Icons.Book /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_subjects}</h3>
                        <p>Active Subjects</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrapper orange"><Icons.Check /></div>
                    <div className="stat-content">
                        <h3>{overall_stats.total_marks}</h3>
                        <p>Assessments Logged</p>
                    </div>
                </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-section">
                <div className="chart-card full-width">
                    <h3>Pass Rates by Subject</h3>
                    <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={groupedPassRates}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="display_name" tick={{ fontSize: 12 }} height={60} interval={0} angle={-15} textAnchor="end" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="pass_rate" fill="#4CAF50" name="Pass Rate (%)" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="average_percentage" fill="#81C784" name="Average (%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Performance by Grade</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={performance_by_grade}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="grade_level" tickFormatter={v => `Gr ${v}`} />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="average_percentage" stroke="#4CAF50" strokeWidth={3} name="Average (%)" />
                            <Line type="monotone" dataKey="pass_rate" stroke="#FF9800" strokeWidth={3} name="Pass Rate (%)" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="chart-card">
                    <h3>Subjects Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={subject_distribution}
                                cx="50%" cy="50%"
                                innerRadius={60} outerRadius={100}
                                dataKey="subject_count"
                            >
                                {subject_distribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Tables Section */}
            <div className="tables-grid">

                {/* Top Performers */}
                <div className="table-card">
                    <div className="card-header">
                        <h3>🏆 Top Performers</h3>
                    </div>
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Student</th>
                                <th>Grade</th>
                                <th>Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top_performers.map((s, i) => (
                                <tr key={s.student_id}>
                                    <td><span className="rank-badge">#{i + 1}</span></td>
                                    <td>{s.name}</td>
                                    <td>{s.grade_level}</td>
                                    <td className="highlight-text">{s.average_percentage}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Needs Support */}
                <div className="table-card">
                    <div className="card-header">
                        <h3>⚠️ Students Needing Support</h3>
                    </div>
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Priority</th>
                                <th>Student</th>
                                <th>Grade</th>
                                <th>Avg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bottom_performers.map((s, i) => (
                                <tr key={s.student_id}>
                                    <td>
                                        <span className={`priority-badge ${i < 3 ? 'high' : 'med'}`}>
                                            {i < 3 ? 'High' : 'Med'}
                                        </span>
                                    </td>
                                    <td>{s.name}</td>
                                    <td>{s.grade_level}</td>
                                    <td className="danger-text">{s.average_percentage}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Support By Grade */}
            {bottom_performers_by_grade && (
                <div className="detailed-section">
                    <h3>Intervention Required by Grade</h3>
                    <div className="support-grid">
                        {bottom_performers_by_grade.map((grade) => (
                            <div key={grade.grade_level} className="support-card">
                                <div className="support-header">
                                    <h4>Grade {grade.grade_level}</h4>
                                    <span className="count-badge">{grade.students.length} Students</span>
                                </div>
                                <div className="student-list">
                                    {grade.students.map(s => (
                                        <div key={s.student_id} className="student-row">
                                            <span>{s.name}</span>
                                            <span className="score">{s.average_percentage}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}